import React, { useState } from "react";
import { Lock, KeyRound, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useAuth from "../contexts/AuthContext";
import { useToast } from "../common_components/Toast";

const ChangePassword = () => {
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  const toast = useToast();

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset errors
    setErrors({});

    // Client-side validations
    const newErrors: Record<string, string> = {};
    if (!formData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }
    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "New password must be at least 6 characters";
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setErrors({ confirmPassword: "Passwords do not match" });
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setErrors({ newPassword: "New password must be different from current password" });
      return;
    }

    setIsLoading(true);
    try {
      const res = await apiRequest("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        }),
      });

      if (res.success) {
        toast.success("Password changed successfully!");
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        // Redirect to homepage or user profile
        navigate("/");
      } else {
        toast.error(res.message || "Failed to change password");
      }
    } catch (err: any) {
      toast.error(err.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 flex items-center justify-center p-4">
      {/* Background blobs for premium depth */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-32 w-96 h-96 bg-blue-400/20 rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-32 w-96 h-96 bg-indigo-400/20 rounded-full opacity-30 blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-white border border-slate-100 rounded-3xl shadow-2xl p-8 backdrop-blur-md bg-white/95">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-lg shadow-blue-500/20 mb-4 animate-pulse">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2 font-serif">
              Change Password
            </h1>
            <p className="text-slate-500 text-sm">
              Please enter your current password to update it
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Current Password Field */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Current Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  name="currentPassword"
                  type="password"
                  placeholder="Enter current password"
                  className={`w-full pl-11 pr-4 py-3.5 border rounded-2xl focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500 bg-slate-50/30 transition-all ${
                    errors.currentPassword
                      ? "border-red-300 focus:ring-red-500/25 focus:border-red-500"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  value={formData.currentPassword}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>
              {errors.currentPassword && (
                <p className="text-xs text-red-600 flex items-center gap-1 font-medium pl-1">
                  <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                  {errors.currentPassword}
                </p>
              )}
            </div>

            {/* New Password Field */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  name="newPassword"
                  type="password"
                  placeholder="Enter new password (min. 6 chars)"
                  className={`w-full pl-11 pr-4 py-3.5 border rounded-2xl focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500 bg-slate-50/30 transition-all ${
                    errors.newPassword
                      ? "border-red-300 focus:ring-red-500/25 focus:border-red-500"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  value={formData.newPassword}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>
              {errors.newPassword && (
                <p className="text-xs text-red-600 flex items-center gap-1 font-medium pl-1">
                  <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                  {errors.newPassword}
                </p>
              )}
            </div>

            {/* Confirm New Password Field */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm new password"
                  className={`w-full pl-11 pr-4 py-3.5 border rounded-2xl focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500 bg-slate-50/30 transition-all ${
                    errors.confirmPassword
                      ? "border-red-300 focus:ring-red-500/25 focus:border-red-500"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-xs text-red-600 flex items-center gap-1 font-medium pl-1">
                  <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full mt-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-400 text-white font-bold py-3.5 px-4 rounded-2xl transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:shadow-none group"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Updating Password...
                </>
              ) : (
                <>
                  Change Password
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* Cancel button */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate(-1)}
              className="text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
              disabled={isLoading}
            >
              Cancel & Go Back
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-400">
            © 2025 AIIMS Mangalagiri. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
